import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, Input, Select, SelectOption, Tabs } from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import { PortalModulesDocument } from '../../graphql/__generated__/types';
import AccountsList from './AccountsList';
import GroupedAccounts from './GroupedAccounts';

const SEARCH_DEBOUNCE_MS = 300;

/** `ALL` is the absence of the argument, and travels as null. */
type PortalFilter = 'ALL' | 'OPEN' | 'CLOSED';

const AccountsPage = () => {
    const [view, setView] = useState<0 | 1>(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [portal, setPortal] = useState<PortalFilter>('ALL');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: modulesData } = useQuery(PortalModulesDocument);
    const moduleTotal = modulesData?.portalModules?.length ?? null;

    const portalEnabled = portal === 'ALL' ? null : portal === 'OPEN';

    const portalOptions: SelectOption<PortalFilter>[] = [
        { label: c('Accounts filter').t`Open and closed`, value: 'ALL' },
        { label: c('Accounts filter').t`Open to the portal`, value: 'OPEN' },
        { label: c('Accounts filter').t`Closed to the portal`, value: 'CLOSED' },
    ];

    const filtered = Boolean(search) || portal !== 'ALL';

    // Search forces the flat view: the grouped view searches companies by name only, so a
    // child account would never surface. Decided on the raw input to switch before the debounce.
    const effectiveView = search.trim() ? 1 : view;

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setPortal('ALL');
    };

    const toolbar = (
        <div className="flex w-full flex-wrap items-center gap-3">
            <Input
                name="account-search"
                value={search}
                onValue={setSearch}
                clearable
                prefix={<MagnifyingGlass size={16} />}
                placeholder={c('Accounts filter').t`Account name or SIREN`}
                aria-label={c('Accounts filter').t`Search an account`}
                className="min-w-0 flex-1 basis-[26rem]"
            />
            <Select
                options={portalOptions}
                value={portalOptions.find((option) => option.value === portal)}
                onChange={(option) => setPortal(option.value)}
                aria-label={c('Accounts filter').t`Filter on the portal access`}
                className="max-w-xs"
            />
            {filtered && (
                <Button category="weak" size="small" onClick={clearFilters}>
                    {c('Accounts').t`Clear filters`}
                </Button>
            )}
        </div>
    );

    const tabs = useMemo(
        () => [
            {
                label: c('Accounts view').t`Group by company`,
                content: (
                    <GroupedAccounts
                        debouncedSearch={debouncedSearch}
                        portalEnabled={portalEnabled}
                        moduleTotal={moduleTotal}
                    />
                ),
            },
            {
                label: c('Accounts view').t`Flat view`,
                content: (
                    <AccountsList
                        debouncedSearch={debouncedSearch}
                        portalEnabled={portalEnabled}
                        moduleTotal={moduleTotal}
                        showCompany
                        label={c('Accounts').t`Accounts list`}
                        emptyTitle={c('Accounts').t`No account in your portfolio.`}
                        emptyDescription={c('Accounts')
                            .t`Nothing was refused: the accounts you may act on are simply none for now. An administrator assigns them, account by account, unless your role sees them all.`}
                    />
                ),
            },
        ],
        [debouncedSearch, portalEnabled, moduleTotal]
    );

    return (
        <section className="flex flex-col gap-5">
            <header>
                <h1 className="text-xl font-semibold text-text-900">{c('Accounts').t`Accounts`}</h1>
            </header>

            <Tabs
                variant="segmented"
                tabs={tabs}
                selectedIndex={effectiveView}
                onChange={(index) => setView(index as 0 | 1)}
                toolbar={toolbar}
                toolbarLeading
            />
        </section>
    );
};

export default AccountsPage;
